import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Download, Loader2, ShieldCheck } from "lucide-react";
import { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Marco, descargarPublico } from "@/pages/FirmarContrato";
import { DocumentoCert } from "@/components/certificaciones/publica/DocumentoCert";
import { FirmaCertForm } from "@/components/certificaciones/publica/FirmaCertForm";
import { insigniaEmitida } from "@/components/certificaciones/utils";

const pub = axios.create({ baseURL: `${API}/certificaciones/publica` });
const fechaHora = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", { dateStyle: "long", timeStyle: "short" }) : "");
const ETQ = "Certificación de obra";

function Verificada({ v, onPdf }) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-cyan-200 bg-cyan-50 p-4 sm:flex-row sm:items-center sm:p-5" data-testid="cert-publica-verificada">
      <ShieldCheck className="w-8 h-8 shrink-0 text-[#00B4CC]" />
      <div className="min-w-0 flex-1">
        <p className="font-bold text-[#1B2B4B]">Certificación auténtica</p>
        <p className="text-sm text-slate-600">Emitida por {v.empresa}. Estado actual: <b data-testid="cert-publica-verificada-estado">{insigniaEmitida(v.estado)[0]}</b>{v.firma ? ` · Firmada por ${v.firma.firmante} el ${fechaHora(v.firma.fecha)}` : ""}.</p>
      </div>
      <Button variant="outline" onClick={onPdf} className="shrink-0" data-testid="cert-publica-pdf"><Download className="w-4 h-4 mr-1.5" /> Descargar PDF</Button>
    </div>
  );
}

function Estado({ v, onPdf }) {
  if (v.verificacion) return <Verificada v={v} onPdf={onPdf} />;
  const firmada = !!v.firma;
  return (
    <div className={`flex flex-col gap-3 rounded-2xl p-4 sm:flex-row sm:items-center sm:p-5 ${firmada ? "border border-emerald-200 bg-emerald-50" : "border border-slate-200 bg-white"}`} data-testid="cert-publica-estado">
      {firmada && <CheckCircle2 className="w-8 h-8 shrink-0 text-emerald-600" />}
      <div className="min-w-0 flex-1">
        <p className="font-bold text-[#1B2B4B]">{firmada ? "Certificación firmada" : `${v.empresa} te ha enviado esta certificación`}</p>
        <p className="text-sm text-slate-600">
          {firmada ? `Firmada por ${v.firma.firmante} el ${fechaHora(v.firma.fecha)}. Puedes descargar tu copia en PDF.`
            : v.puede_firmar ? "Revisa el avance y los importes. Al final encontrarás el recuadro para firmar." : "La empresa está preparando esta certificación. Podrás firmarla cuando te la envíe de nuevo."}
        </p>
      </div>
      <Button variant="outline" onClick={onPdf} className="shrink-0" data-testid="cert-publica-pdf"><Download className="w-4 h-4 mr-1.5" /> Descargar PDF</Button>
    </div>
  );
}

export default function FirmarCertificacion() {
  const { token } = useParams();
  const [v, setV] = useState(null);
  const [error, setError] = useState("");
  const cargar = useCallback(() => pub.get(`/${token}`).then((r) => setV(r.data))
    .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Enlace no válido")), [token]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { if (v) document.title = `Certificación ${v.numero} · ${v.empresa}`; }, [v]);
  const firmar = async (payload) => {
    try { await pub.post(`/${token}/firmar`, payload); toast.success("Certificación firmada. ¡Gracias!"); await cargar(); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); if (e.response?.status === 409) cargar(); }
  };
  const pdf = () => descargarPublico(`${API}/certificaciones/publica/${token}/pdf`, `certificacion_${v.numero}.pdf`).catch(() => toast.error("No se pudo descargar el PDF"));
  if (error) return <Marco etiqueta={ETQ}><div className="rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="cert-publica-error"><AlertTriangle className="mx-auto mb-3 w-10 h-10 text-amber-500" /><p className="font-semibold text-[#1B2B4B]">{error}</p></div></Marco>;
  if (!v) return <Marco etiqueta={ETQ}><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#00B4CC]" /></div></Marco>;
  return (
    <Marco empresa={v.empresa} logo={v.logo} etiqueta={ETQ}>
      <Estado v={v} onPdf={pdf} />
      <DocumentoCert v={v} />
      {v.puede_firmar && !v.firma && <FirmaCertForm v={v} onFirmar={firmar} />}
    </Marco>
  );
}
